import React, { useContext, useEffect } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { useTheme } from '@emotion/react';
import { debounce } from 'lodash';
import {
  type ExperienceMultiRowSurveyCard,
  type ExperienceMultiSurveyCardConfirmationSection,
} from '@bbkAdminRedux/rtkq/experience.slice';
import { cs } from '@bbkAdminUtils/css-utils';
import { type Maybe } from '@bbkAdminUtils/utility-types';
import { FunnelContext } from '@bbkFunnel/components/FunnelContext';
import { FunnelButton } from '@bbkFunnel/components/v2';
import { type BackwardsObject } from '@bbkFunnel/pages/Funnel';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import FunnelCheckbox from '@bbkFunnelComponents/checkbox/funnel-checkbox';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';

import './submit-section.scss';

type Props = {
  questions: {
    confirmation: ExperienceMultiSurveyCardConfirmationSection;
  };
  handleCheck: (e: React.ChangeEvent<HTMLInputElement>, value: boolean) => void;
  checked: boolean;
  loading?: boolean;
  card: ExperienceMultiRowSurveyCard;
  handleBackwards: (backObj: BackwardsObject) => void;
  handleForwards: () => void;
  className?: string;
  onClick?: React.MouseEventHandler;
};

export const SubmitSection: React.FC<Props> = ({
  questions,
  handleCheck,
  checked,
  loading,
  card,
  card: {
    id,
    name,
    style_overlay: { buttonOrder, button_order, buttonColors },
    requires: { backwards, forwards },
  },
  handleBackwards,
  handleForwards,
  className,
  onClick,
}) => {
  const theme = useTheme() as EmotionTheme;
  const { isDesigner } = useContext(FunnelContext);
  const { confirmation: confQuestion } = questions;

  useEffect(() => {
    const muiRoot = document.querySelector('.MuiPaper-root');
    const exitFunnelEl = document.querySelector(
      '.exit-funnel'
    ) as Maybe<HTMLElement>;
    const attachEl = muiRoot ?? window;
    const container = muiRoot ?? document.documentElement;

    const footer = document.querySelector('.survey-submit-sticky');
    if (!container || !footer) return undefined;

    let containerHeight: number;
    let footerHeight: number;
    const calcHeights = () => {
      containerHeight = container.scrollHeight;
      footerHeight = footer.scrollHeight;
    };
    setTimeout(() => calcHeights(), 100);

    const nonDebouncedHandler = () => {
      const containerPaddingBottom = Math.max(228, footerHeight);
      const windowHeight = window.innerHeight;
      const scrollTop = container.scrollTop;
      const atBottomNum = containerHeight - scrollTop - windowHeight;
      // console.log(
      //   containerHeight,
      //   scrollTop,
      //   containerPaddingBottom,
      //   windowHeight,
      //   footerHeight,
      //   atBottomNum
      // );
      const atBottom = atBottomNum <= 0;
      if (atBottom) {
        // We are at the bottom of the page
        container.classList.remove('sticky-footer-enabled');
        if (exitFunnelEl) {
          exitFunnelEl.style.paddingBottom = '0px';
        }
      } else {
        container.classList.add('sticky-footer-enabled');
        if (exitFunnelEl) {
          exitFunnelEl.style.paddingBottom = `${containerPaddingBottom}px`;
        }
      }
    };

    const handler = debounce(nonDebouncedHandler, 5);

    attachEl.addEventListener('scroll', handler, false);
    window.addEventListener('resize', handler, false);
    setTimeout(nonDebouncedHandler, 200);
    return () => {
      attachEl.removeEventListener('scroll', handler, false);
      window.removeEventListener('resize', handler, false);
    };
  }, []);

  const mainBtnColor =
    theme.buttonBackgroundColor || theme.primaryColor || 'inherit';
  const backgroundColor = new TinyColor(theme.cardBackground)
    .darken(3)
    .toHexString();

  const outlineButtonStyles = {
    border: `2px solid ${mainBtnColor}`,
    backgroundColor: 'transparent',
    color: mainBtnColor,
  };
  const filledButtonStyles = {
    border: `2px solid ${mainBtnColor}`,
  };

  const themeButtonOrder =
    buttonOrder || button_order || theme.submitButtonOrder;
  const themeButtonMode = buttonColors || theme.submitButtonMode;

  const btnOrderStyles = button_order ?? {
    flexDirection: themeButtonOrder === 'reversed' ? 'row-reverse' : 'row',
  };
  const buttonColorsReversed =
    themeButtonMode === 'reversed' || themeButtonMode === 'dark';

  const confirmCancelButton = (
    <FunnelButton
      classes="bbk-submit-confirm-cancel-button"
      onClick={handleForwards}
      disabled={
        (confQuestion.is_required && !checked && !confQuestion.hide_checkbox) ||
        loading
      }
      specificStyles={
        buttonColorsReversed ? filledButtonStyles : outlineButtonStyles
      }
    >
      {loading ? (
        'Please wait...'
      ) : (
        <CustomerHtml value={card.requires.forwards.text} />
      )}
    </FunnelButton>
  );
  const deflectButton = (
    <FunnelButton
      onClick={(e) =>
        handleBackwards({
          name: 'submit_section_deflect_button',
          component: {
            id,
            name,
          },
          direction: 'backwards',
          type: backwards.type,
          action: backwards.action,
          url: backwards.url,
          validation: backwards.validation,
          form: backwards.sync,
        })
      }
      specificStyles={
        buttonColorsReversed ? outlineButtonStyles : filledButtonStyles
      }
      classes="bbk-deflect-button"
    >
      <CustomerHtml value={card.requires.backwards.text} />
    </FunnelButton>
  );

  return (
    <div
      className={cs('survey-submit', className)}
      onClick={onClick}
      css={{ background: backgroundColor }}
    >
      <div className="survey-submit-inner">
        {confQuestion.is_required && (
          <div className="confirm-text">
            <FunnelCheckbox
              label={confQuestion.label}
              handleChange={(e) => handleCheck(e, !checked)}
              checked={checked}
              name="confirmation"
              hide_checkbox={confQuestion.hide_checkbox}
            />
          </div>
        )}
        <div className="confirm-btns" style={btnOrderStyles}>
          {(!forwards.disabled || isDesigner) && (
            <span
              className="cancel-btn forward-btn"
              css={{ opacity: `${!forwards.disabled ? '1' : '0.5'}` }}
            >
              {confirmCancelButton}
            </span>
          )}
          {(!backwards.disabled || isDesigner) && (
            <span
              className="backwards-btn"
              css={{ opacity: `${!backwards.disabled ? '1' : '0.5'}` }}
            >
              {deflectButton}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
